import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DataMap.css';

// Create a simple default marker icon to avoid configuration issues
const createDefaultIcon = () => {
  return L.divIcon({
    html: '<div style="background-color: #3388ff; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"></div>',
    iconSize: [16, 16],
    className: 'simple-marker'
  });
};

// Component to handle map fitting to bounds
function MapBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);
  
  return null;
}

// Component for heat map layer
function HeatMapLayer({ data, heatmapField }) {
  const map = useMap();
  
  useEffect(() => {
    if (!data || !heatmapField || !window.L?.heatLayer) return;

    // Prepare heat map data
    const heatData = data
      .filter(item => item.Latitude && item.Longitude && item[heatmapField] !== null && item[heatmapField] !== undefined)
      .map(item => [
        parseFloat(item.Latitude),
        parseFloat(item.Longitude),
        parseFloat(item[heatmapField]) || 0
      ]);

    if (heatData.length === 0) return;

    // Create heat layer
    const heatLayer = window.L.heatLayer(heatData, {
      radius: 25,
      blur: 15,
      maxZoom: 17,
      gradient: {
        0.0: 'blue',
        0.5: 'green', 
        0.7: 'yellow',
        1.0: 'red'
      }
    });

    heatLayer.addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, data, heatmapField]);

  return null;
}

const DataMap = ({ 
  data = [], 
  columns = [], 
  title = "Data Map",
  height = "600px"
}) => {
  const [labelField, setLabelField] = useState('WellName');
  const [colorField, setColorField] = useState('');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapField, setHeatmapField] = useState('');
  const [mapType, setMapType] = useState('OpenStreetMap');

  // Filter data with valid coordinates
  const validData = useMemo(() => {
    return data.filter(item => 
      item.Latitude && 
      item.Longitude && 
      !isNaN(parseFloat(item.Latitude)) && 
      !isNaN(parseFloat(item.Longitude))
    );
  }, [data]);

  // Calculate map bounds
  const bounds = useMemo(() => {
    if (validData.length === 0) return null;
    
    const lats = validData.map(item => parseFloat(item.Latitude));
    const lngs = validData.map(item => parseFloat(item.Longitude));
    
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    ];
  }, [validData]);

  // Get numeric columns for heat map
  const numericColumns = useMemo(() => {
    return columns.filter(col => 
      ['decimal4', 'decimal2', 'float', 'comma', 'currency', 'int'].includes(col.type?.toLowerCase())
    );
  }, [columns]);

  // Get all columns for labeling
  const labelColumns = useMemo(() => {
    return columns.filter(col => col.name !== 'WellID');
  }, [columns]);

  // Color mapping for categorical data
  const getMarkerColor = (value, field) => {
    if (!colorField || !value) return '#3388ff'; // Default blue
    
    const colors = ['#ff4444', '#44ff44', '#4444ff', '#ff44ff', '#44ffff', '#ffff44'];
    const hash = value.toString().split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Create custom icon based on color
  const createCustomIcon = (color) => {
    try {
      return L.divIcon({
        html: `<div style="background-color: ${color}; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"></div>`,
        iconSize: [18, 18],
        className: 'custom-marker'
      });
    } catch (error) {
      console.warn('Failed to create custom icon, using default:', error);
      return createDefaultIcon(); // Fall back to our simple default icon
    }
  };

  // Map tile layers
  const tileLayers = {
    OpenStreetMap: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    Satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    Terrain: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'
  };

  // Default center if no data
  const defaultCenter = [13.7563, 100.5018]; // Bangkok, Thailand
  const center = bounds ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2] : defaultCenter;

  // Load heat map plugin
  useEffect(() => {
    if (showHeatmap && !window.L?.heatLayer) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js';
      script.async = true;
      document.head.appendChild(script);
      
      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [showHeatmap]);

  return (
    <div className="data-map-container">
      <div className="map-controls">
        <h3>{title}</h3>
        
        <div className="controls-row">
          <div className="control-group">
            <label>Label Field:</label>
            <select 
              value={labelField} 
              onChange={(e) => setLabelField(e.target.value)}
            >
              {labelColumns.map(col => (
                <option key={col.name} value={col.name}>
                  {col.label || col.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Color By:</label>
            <select 
              value={colorField} 
              onChange={(e) => setColorField(e.target.value)}
            >
              <option value="">Default</option>
              {labelColumns.map(col => (
                <option key={col.name} value={col.name}>
                  {col.label || col.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Map Type:</label>
            <select 
              value={mapType} 
              onChange={(e) => setMapType(e.target.value)}
            >
              {Object.keys(tileLayers).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {numericColumns.length > 0 && (
          <div className="controls-row">
            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                />
                Show Heat Map
              </label>
            </div>

            {showHeatmap && (
              <div className="control-group">
                <label>Heat Map Field:</label>
                <select 
                  value={heatmapField} 
                  onChange={(e) => setHeatmapField(e.target.value)}
                >
                  <option value="">Select Field</option>
                  {numericColumns.map(col => (
                    <option key={col.name} value={col.name}>
                      {col.label || col.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div className="map-stats">
          <span>Total Wells: {data.length}</span>
          <span>Mapped Wells: {validData.length}</span>
          {validData.length < data.length && (
            <span className="warning">
              {data.length - validData.length} wells missing coordinates
            </span>
          )}
        </div>
      </div>

      <div className="map-wrapper" style={{ height }}>
        {validData.length > 0 ? (
          <MapContainer
            center={center}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            key={`${mapType}-${bounds ? bounds.toString() : 'default'}`}
          >
            <TileLayer
              url={tileLayers[mapType]}
              attribution={
                mapType === 'OpenStreetMap' 
                  ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  : mapType === 'Satellite'
                  ? '&copy; <a href="https://www.esri.com/">Esri</a>'
                  : '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
              }
            />
            
            {bounds && <MapBounds bounds={bounds} />}
            
            {showHeatmap && heatmapField && (
              <HeatMapLayer data={validData} heatmapField={heatmapField} />
            )}
            
            {!showHeatmap && validData.map((item, index) => {
              const lat = parseFloat(item.Latitude);
              const lng = parseFloat(item.Longitude);
              
              // Skip invalid coordinates
              if (isNaN(lat) || isNaN(lng)) return null;
              
              const color = getMarkerColor(item[colorField], colorField);
              const markerIcon = colorField ? createCustomIcon(color) : createDefaultIcon();
              
              return (
                <Marker
                  key={`${item.WellID || index}-${lat}-${lng}`}
                  position={[lat, lng]}
                  icon={markerIcon}
                >
                  <Popup>
                    <div className="popup-content">
                      <h4>{item[labelField] || 'Unknown'}</h4>
                      <div className="popup-details">
                        {columns
                          .filter(col => col.name !== 'WellID' && item[col.name] !== null && item[col.name] !== undefined && item[col.name] !== '')
                          .slice(0, 8) // Limit to first 8 non-empty fields
                          .map(col => (
                            <div key={col.name} className="popup-row">
                              <span className="popup-label">{col.label || col.name}:</span>
                              <span className="popup-value">
                                {col.type === 'date' && item[col.name] 
                                  ? new Date(item[col.name]).toLocaleDateString()
                                  : col.type === 'comma' && !isNaN(item[col.name])
                                  ? parseFloat(item[col.name]).toLocaleString()
                                  : col.type === 'currency' && !isNaN(item[col.name])
                                  ? '$' + parseFloat(item[col.name]).toLocaleString()
                                  : item[col.name]
                                }
                                {col.unit && ` ${col.unit}`}
                              </span>
                            </div>
                          ))
                        }
                        <div className="popup-row">
                          <span className="popup-label">Coordinates:</span>
                          <span className="popup-value">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        ) : (
          <div className="no-data-message">
            <h3>No mappable data available</h3>
            <p>No wells have valid latitude and longitude coordinates.</p>
            {data.length > 0 && (
              <p>
                Total wells: {data.length}<br/>
                Please ensure wells have valid Latitude and Longitude values to display on the map.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataMap; 